import { signupDestination } from './utils/attribution';
import { resolveLandingRoute } from './features/marketing/landings/resolveLandingRoute';
import { createLandingPages } from './features/marketing/landings/landingPageMap';
import { landingById, landingPageKey } from './features/marketing/landings/landingRegistry';
import TutorBand, { showsCalculTutorBand } from './features/marketing/landings/TutorBand';
import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { Loader } from 'lucide-react';
import './App.css';
import { useTokenMonitor } from './hooks/useTokenMonitor';
import { useInactivityMonitor } from './hooks/useInactivityMonitor';
import api from './utils/api';
import { URSSAF_INTENT, URSSAF_STATE } from './constants/urssafStatus';
import { parseRegisterLink, parseRetryLink } from './features/urssaf/urssafRetryLink';
import { hasLiveStoredSession, needsSilentRefresh } from './utils/storedSession';
import { resolvePortfolioRoute } from './features/portfolio/utils/slug';
import { resolveFormationsRoute } from './features/formations/formationsHosts';
import { isPublicExamPath } from './features/examens/examChamberConstants';
import { EXAM_BAC_PATH, EXAM_CATALOG_PATH } from './constants/examSetup';
import { resolveExamHostRoute } from './features/examens/examHosts';
import { STANDALONE_PATH, isStandalonePath } from './app/standaloneRoutes';
import MarketingShell, { isMarketingPath } from './features/marketing/MarketingShell';
import Footer from './components/layout/Footer';
import { CALCUL_FOOTER, EXAM_FOOTER } from './constants/publicFooter';
import { RecordNavigationProvider } from './contexts/RecordNavigationContext';
import RecordDetailRoute from './app/RecordDetailRoute';
import { FAMILY_TAB_PARAM, RECORD_PATH, familyPath, familySpacePath, guidePathFor, studentPath } from './constants/recordPaths';
import { FORM_STEP_PARAM } from './constants/formSteps';
import NetworkBanner from './components/layout/NetworkBanner';
import { TEACHER_FAMILY_REQUESTS_PATH } from './constants/familySpace';
import { SETTINGS_PATH, SETTINGS_SECTION, settingsPath } from './constants/settings';
import { NOTIFICATIONS_PATH } from './constants/inbox';
import { InboxProvider } from './hooks/useInbox';
import { CommandPaletteProvider } from './features/search/CommandPaletteProvider';
import { GuideDemoProvider } from './features/guides/GuideDemoContext';
import { APP_HOME_PATH, RELANCE_ALL } from './constants/home';
import { SESSION_STATE_KEY } from './constants/sessionState';
import useSessionState, { clearSessionState } from './hooks/useSessionState';
import useAuthAccessFeedback from './hooks/useAuthAccessFeedback';
import { AUTH_ACCESS_COPY } from './constants/auth';
import { PUBLIC_PATH } from './constants/publicPaths';
import { PAYMENT_DATA_CHANGED_EVENT } from './constants/paymentEvents';
import ShellMain from './components/layout/ShellMain';
import PageFrame from './components/ui/PageFrame';
import { PAGE_WIDTH } from './constants/layout';
import { PEOPLE_COPY } from './constants/people_messages.fr';
import { INVOICE_COPY } from './constants/invoice_messages.fr';
import { NEW_ACTION } from './features/newMenu/newMenuModel';
import { toolById } from './features/tools/toolRegistry';

/** VIS1: form routes sit in a narrow PageFrame under their section title. */
const formPage = (title, form) => (
  <PageFrame width={PAGE_WIDTH.NARROW}>
    <PageFrame.Header title={title} />
    <PageFrame.Body>{form}</PageFrame.Body>
  </PageFrame>
);

/*
 * EVERY route component below is lazy, and that is a rule rather than an
 * optimisation.
 *
 * App.js is the one module every visitor downloads, and most visitors are not
 * teachers: a parent opening mahmoud-tulaimat.neoprof.fr wants a landing page,
 * not a dashboard. Statically importing a route pulls its whole dependency tree
 * — recharts, katex, dnd-kit — into the entry chunk, where it is downloaded and
 * parsed on a phone that will never sign in.
 *
 * The imports kept static above are the ones genuinely used on every path:
 * the auth/session hooks (called unconditionally, before any branch), the api
 * client they need, the route resolver, and the two providers plus the banner
 * that wrap the shell.
 *
 * If you add a route, add it here as lazy() and render it under a Suspense.
 */

// Public — reachable without a session.
const PortfolioPage = lazy(() => import('./features/portfolio/PortfolioPage'));
const PortfolioDashboard = lazy(() => import('./features/portfolio/PortfolioDashboard'));
const PortfolioEditor = lazy(() => import('./features/portfolio/PortfolioEditor'));
const ProductDemoPage = lazy(() => import('./features/marketing/pages/PublicLanding'));
const FeaturesPage = lazy(() => import('./features/marketing/pages/PublicLandingCourseTransform'));
const HomeLanding = lazy(() => import('./features/marketing/landing/HomeLanding'));
const NeoCalculLanding = lazy(() => import('./features/marketing/neocalcul/NeoCalculLanding'));
const EditorialPage = lazy(() => import('./features/editorial/EditorialPage'));
const ColdHandsPage = lazy(() => import('./features/coldHands/ColdHandsPage'));
const FamilySpaceApp = lazy(() => import('./features/familySpace/FamilySpaceApp'));
const FormationsApp = lazy(() => import('./features/formations/FormationsApp'));
const Login = lazy(() => import('./features/auth/login.js'));
const Register = lazy(() => import('./features/auth/Register'));
const SharedMissions = lazy(() => import('./pages/SharedMissions'));
const PublicGame = lazy(() => import('./pages/PublicGame'));
const PublicTest = lazy(() => import('./pages/PublicTest'));
const CalculHome = lazy(() => import('./pages/Calcul/CalculHome'));
const CalculGuidePage = lazy(() => import('./pages/Calcul/CalculGuidePage'));
const CalculReport = lazy(() => import('./pages/Calcul/CalculReport'));
const CalculPlay = lazy(() => import('./pages/Calcul/CalculPlay'));
const CalculPrint = lazy(() => import('./pages/Calcul/CalculPrint'));
const NombresEnLettres = lazy(() => import('./pages/Calcul/NombresEnLettres'));
const ScratchAtelier = lazy(() => import('./pages/Calcul/ScratchAtelier'));
const ActivityPlaylistsPage = lazy(() => import('./pages/Activities/ActivityPlaylistsPage'));
const ActivityPlaylistPlayPage = lazy(() => import('./pages/Activities/ActivityPlaylistPlayPage'));
const ExamensHome = lazy(() => import('./pages/Examens/ExamensHome'));
const ExamSubjects = lazy(() => import('./pages/Examens/ExamSubjects'));
const ExamResourceCatalog = lazy(() => import('./pages/Examens/ExamResourceCatalog'));
const PublicExamExperience = lazy(() => import('./features/examens/public/PublicExamExperience'));
const ExamPaperPage = lazy(() => import('./pages/Examens/ExamPaperPage'));
const ExamPlay = lazy(() => import('./pages/Examens/ExamPlay'));
const ExamResult = lazy(() => import('./pages/Examens/ExamResult'));
const ExamHostPage = lazy(() => import('./pages/Examens/ExamHostPage'));
const InvoiceVerification = lazy(() => import('./features/billing/InvoiceVerification'));
const ThemeDemo = lazy(() => import('./pages/ThemeDemo'));

// Legal — linked from every footer, opened by almost no one.
const LegalMentions = lazy(() => import('./features/legal/LegalMentions'));
const PrivacyPolicy = lazy(() => import('./features/legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./features/legal/TermsOfService'));
const TermsOfSale = lazy(() => import('./features/legal/TermsOfSale'));
const ContactUs = lazy(() => import('./features/marketing/pages/ContactUs'));
const AvanceImmediate = lazy(() => import('./features/urssaf/AvanceImmediate'));
const AvanceImmediateCinematic = lazy(() => import('./features/marketing/landings/avanceImmediate/AvanceImmediateLanding'));
// Prerendered marketing routes (LAND3): lazy here, eager in the prerender entry.
const MARKETING_PAGES = Object.freeze({
  '/': HomeLanding,
  '/avance-immediate': AvanceImmediate,
  ...createLandingPages({ avanceImmediate: AvanceImmediateCinematic, neocalcul: NeoCalculLanding }),
  '/fonctionnalites': FeaturesPage,
  '/demonstration': ProductDemoPage,
  '/contact': ContactUs,
});
const BillingPage = lazy(() => import('./features/billing/BillingPage'));
const SettingsPage = lazy(() => import('./features/settings/SettingsPage'));
const NotificationsPage = lazy(() => import('./features/inbox/NotificationsPage'));
const StatisticsLock = lazy(() => import('./features/billing/components/StatisticsLock'));
const TrialEndNotice = lazy(() => import('./features/billing/components/TrialEndNotice'));

// Authenticated shell chrome — mounted once a teacher is signed in.
const Navigation = lazy(() => import('./components/layout/Navigation'));
const FirstUseGuide = lazy(() => import('./features/guides/FirstUseGuide'));
const GlobalCourseNotification = lazy(() => import('./features/course_sessions/GlobalCourseNotification'));
const TokenExpirationWarning = lazy(() => import('./features/auth/TokenExpirationWarning'));
const SessionExpiredOverlay = lazy(() => import('./features/auth/SessionExpiredOverlay'));
const UrssafRegistrationModal = lazy(() => import('./features/urssaf/UrssafRegistrationModal'));

// Authenticated routes — the heavy half of the application.
const Home = lazy(() => import('./features/home/Home'));
const StudentList = lazy(() => import('./features/records/StudentList'));
const StudentForm = lazy(() => import('./features/records/StudentForm'));
const StudentRecord = lazy(() => import('./features/records/StudentRecord'));
const FamilyList = lazy(() => import('./features/records/FamilyList'));
const FamilyRecord = lazy(() => import('./features/records/FamilyRecord'));
const MissionLibrary = lazy(() => import('./features/missions/MissionLibrary'));
const FamilyForm = lazy(() => import('./features/records/FamilyForm'));
const CourseSession = lazy(() => import('./features/course_sessions/CourseSession'));
const Statistics = lazy(() => import('./features/statistics/Statistics'));
const InvoiceGenerator = lazy(() => import('./features/billing/InvoiceGenerator'));
const ScheduleManager = lazy(() => import('./features/course_sessions/ScheduleManager'));
const PaymentForm = lazy(() => import('./features/payments/PaymentForm'));
const PaymentsPage = lazy(() => import('./features/payments/PaymentsPage'));
const LegacyRequestsRedirect = lazy(() => import('./features/inbox/LegacyRequestsRedirect'));
const ModelEditorPage = lazy(() => import('./pages/ModelEditorPage'));
const ExamWizardPage = lazy(() => import('./pages/ExamWizardPage'));
const ExamMonitorPage = lazy(() => import('./pages/ExamMonitorPage'));
const ExamSittingPage = lazy(() => import('./pages/ExamSittingPage'));
const ToolsPage = lazy(() => import('./pages/ToolsPage'));
const LessonPage = lazy(() => import('./pages/LessonPage'));

const PublicPageLoader = () => {
  useAuthAccessFeedback({ pending: true });
  return (
    <div className="loading-screen">
      <Loader size={32} strokeWidth={2} className="loading-screen__spinner" />
    </div>
  );
};

/**
 * A standalone public branch: one lazy route, one Suspense, no app shell.
 *
 * Every public branch had already grown its own identical wrapper; this is the
 * shared one, so adding a route cannot accidentally ship without a boundary.
 */
const StandalonePage = ({
  children,
  footerContent = <Footer />,
}) => (
  <div className="App App--standalone">
    <Suspense fallback={<PublicPageLoader />}>
      <Routes>{children}</Routes>
    </Suspense>
    {footerContent}
  </div>
);

// Free plan: /statistics is Pro-only, so only the lifetime-hours tile is fed.
const fetchDashboardStats = (plan) => (
  plan === 'free'
    ? api.getLifetimeHours()
    : api.getStatistics({ suppressProRedirect: true })
);

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [students, setStudents] = useState([]);
  const [families, setFamilies] = useState([]);
  const [stats, setStats] = useState({});
  const [statisticsLoaded, setStatisticsLoaded] = useState(false);
  const [config, setConfig] = useState({});
  const [messages, setMessages] = useState({});
  const [classLevels, setClassLevels] = useState([]);
  // Selections below survive a tab refresh (sessionStorage); the course session is restored by the backend.
  const [editingStudent, setEditingStudent] = useSessionState(SESSION_STATE_KEY.EDITING_STUDENT);
  const [editingFamily, setEditingFamily] = useSessionState(SESSION_STATE_KEY.EDITING_FAMILY);
  const [selectedStudentForCourse, setSelectedStudentForCourse] = useState(null);
  const [selectedStudentForDetail, setSelectedStudentForDetail] = useSessionState(SESSION_STATE_KEY.STUDENT_DETAIL);
  const [selectedFamilyForDetail,  setSelectedFamilyForDetail]  = useSessionState(SESSION_STATE_KEY.FAMILY_DETAIL);
  const [selectedFamilyForInvoice, setSelectedFamilyForInvoice] = useSessionState(SESSION_STATE_KEY.INVOICE_FAMILY);

  const [pendingStudentFamilyId, setPendingStudentFamilyId] = useSessionState(SESSION_STATE_KEY.PENDING_STUDENT_FAMILY);
  const [urssafModalFamilyId, setUrssafModalFamilyId] = useState(null);
  // URS2: edit-form banner action → { autoStart, isRenew } for the modal opened after the save
  const [urssafModalMode, setUrssafModalMode] = useState(null);
  // Where to navigate after the URSSAF modal is dismissed (success or skip)
  const [urssafPostModalPath, setUrssafPostModalPath] = useState('/families');
  // True when an edit was triggered by "Revenir à la fiche famille" in the URSSAF modal
  const [urssafEditMode, setUrssafEditMode] = useState(false);
  const [selectedFamilyForPayment, setSelectedFamilyForPayment] = useSessionState(SESSION_STATE_KEY.PAYMENT_FAMILY);
  const [schedulePrefill, setSchedulePrefill] = useSessionState(SESSION_STATE_KEY.SCHEDULE_PREFILL); // { student, day_of_week, start_time, start_date, recurrence }
  const [sessionToRestore, setSessionToRestore] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(hasLiveStoredSession);
  // teacher_info stored but token expired: try a silent refresh before showing login
  const [authChecking, setAuthChecking] = useState(needsSilentRefresh);
  const [authAccessError, setAuthAccessError] = useState(null);
  useAuthAccessFeedback({ error: authAccessError, denied: !authChecking && !isAuthenticated });
  const [teacherInfo, setTeacherInfo] = useState(() => {
    const stored = localStorage.getItem('teacher_info');
    return stored ? JSON.parse(stored) : null;
  });
  const [showArchived, setShowArchived] = useState(false);
  const [showArchivedFamilies, setShowArchivedFamilies] = useState(false);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [pendingPaymentsLoaded, setPendingPaymentsLoaded] = useState(false);
  const [showTokenWarning, setShowTokenWarning] = useState(false);
  const [tokenRemainingTime, setTokenRemainingTime] = useState(null);
  const [statsActiveTab, setStatsActiveTab] = useSessionState(SESSION_STATE_KEY.STATS_TAB, 'revenues');
  const [statsScrollTo, setStatsScrollTo] = useState(null);
  const [showSessionExpired, setShowSessionExpired] = useState(false);

  // On startup: if teacher_info exists but token expired, try silent refresh before showing login
  useEffect(() => {
    if (!authChecking) return;
    // Skip when a magic-link verify is about to run: a concurrent silentRefresh
    // failure would race verifyMagicLink and wipe the fresh session it just
    // wrote (csrf_token, session_expires_at), causing the next request to 401
    // and the SessionExpiredOverlay to fire seconds after login.
    if (new URLSearchParams(window.location.search).has('token')) {
      setAuthChecking(false);
      return;
    }
    api.silentRefresh()
      .then(() => {
        setIsAuthenticated(true);
      })
      .catch((error) => {
        setAuthAccessError(error.isNetworkError ? error.message : AUTH_ACCESS_COPY.sessionExpired);
        if (error.isNetworkError) return;
        // Refresh failed — clear stale data, show login
        api.clearToken();
        localStorage.removeItem('teacher_info');
        setTeacherInfo(null);
      })
      .finally(() => setAuthChecking(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Token expiration handlers
  const handleTokenExpired = useCallback(async () => {
    try {
      await api.silentRefresh();
      // Refresh succeeded — dismiss any lingering warning
      setShowTokenWarning(false);
    } catch (error) {
      if (error.isNetworkError) return;
      // Refresh token expired/revoked — show re-auth overlay (all state preserved)
      setShowSessionExpired(true);
    }
  }, []);

  const handleTokenWarning = useCallback(async (remainingTime) => {
    // Proactive refresh: try to renew token silently before alarming the user
    try {
      await api.silentRefresh();
      // Refresh succeeded — token renewed transparently, no warning needed
      setShowTokenWarning(false);
    } catch {
      // Refresh failed — show the warning banner so the user can save their work
      setTokenRemainingTime(remainingTime);
      setShowTokenWarning(true);
    }
  }, []);

  // Monitor token expiration (warns at 10% time remaining)
  useTokenMonitor({
    warningThreshold: 0.10,
    onExpired: handleTokenExpired,
    onWarning: handleTokenWarning,
    isAuthenticated
  });

  // Show overlay after configured inactivity period (server-provided, falls back to 30 min)
  const handleInactive = useCallback(() => {
    setShowSessionExpired(true);
  }, []);
  const inactivityMs = (parseInt(localStorage.getItem('inactivity_timeout_seconds'), 10) || 1800) * 1000;
  useInactivityMonitor(handleInactive, isAuthenticated, inactivityMs);

  // Register session expired callback with API service
  useEffect(() => {
    api.setSessionExpiredCallback(() => {
      setShowSessionExpired(true);
    });

    // Cleanup on unmount
    return () => {
      api.setSessionExpiredCallback(null);
    };
  }, []);

  // Silent background refresh - fetches data directly without triggering loading states
  const refreshAllDataInBackground = async () => {
    try {
      const [
        configData,
        messagesData,
        classLevelsData,
        studentsData,
        familiesData,
        statsData,
        pendingPaymentsData
      ] = await Promise.all([
        api.getConfig().catch(() => null),
        api.getMessages().catch(() => null),
        api.getClassLevels().catch(() => null),
        api.getStudents(showArchived).catch(() => null),
        api.getFamilies(showArchivedFamilies).catch(() => null),
        fetchDashboardStats(teacherInfo?.entitlement?.plan).catch(() => null),
        api.getPendingPayments().catch(() => null)
      ]);

      // Only update state if data was successfully fetched
      if (configData) setConfig(configData);
      if (messagesData) setMessages(messagesData);
      if (classLevelsData) setClassLevels(classLevelsData);
      if (studentsData) setStudents(studentsData);
      if (familiesData) setFamilies(familiesData);
      if (statsData) setStats(prev => ({ ...prev, ...statsData }));
      if (pendingPaymentsData) setPendingPayments(pendingPaymentsData);
    } catch (error) {
      // Silently ignore errors during background refresh
      console.debug('Background refresh error (ignored):', error);
    }
  };

  // Teacher data only feeds the teacher shell: public pages (/family, /calcul…) never load it.
  const teacherShellActive = isAuthenticated
    && !isStandalonePath({ hostname: window.location.hostname, pathname: location.pathname });

  // Load initial data when the teacher shell is shown
  useEffect(() => {
    if (teacherShellActive) {
      loadConfig();
      loadMessages();
      loadClassLevels();
      loadStatistics();
      loadPendingPayments();
    }
  }, [teacherShellActive]);

  // Post-auth: hydrate with the full /teacher/info payload (the session
  // response is a slim subset). Fires the one-shot profile-completion
  // auto-prompt when the flag hasn't been flipped yet AND required fields
  // are still missing. Also re-fires on 'teacher-info-updated' so that
  // saving the profile clears the red dot + bell entries live.
  useEffect(() => {
    if (!teacherShellActive) return;
    let cancelled = false;
    const hydrate = async ({ firePrompt }) => {
      try {
        const full = await api.getTeacherInfo();
        if (cancelled || !full) return;
        setTeacherInfo((prev) => ({ ...(prev || {}), ...full }));
        try {
          localStorage.setItem('teacher_info', JSON.stringify(full));
        } catch {}
        if (!firePrompt) return;
        const shouldPrompt =
          full.profile_autoprompt_shown === false
          && Array.isArray(full.missing_required_fields)
          && full.missing_required_fields.length > 0;
        if (shouldPrompt) {
          navigate(settingsPath(SETTINGS_SECTION.PROFILE));
          api.markProfileAutopromptSeen().catch(() => {});
          setTeacherInfo((prev) => prev ? { ...prev, profile_autoprompt_shown: true } : prev);
        }
      } catch {
        // Non-blocking: the navbar keeps working with the slim session payload.
      }
    };
    hydrate({ firePrompt: true });
    const onUpdate = () => hydrate({ firePrompt: false });
    window.addEventListener('teacher-info-updated', onUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener('teacher-info-updated', onUpdate);
    };
  // navigate changes on every route change; re-hydrating per navigation is not wanted.
  }, [teacherShellActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Automatic data refresh every 5 minutes (300000 ms)
  useEffect(() => {
    if (!teacherShellActive) return;

    const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds

    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible' && navigator.onLine) refreshAllDataInBackground();
    }, REFRESH_INTERVAL);

    // Cleanup interval on unmount or when authentication changes
    return () => {
      clearInterval(intervalId);
    };
  }, [teacherShellActive, showArchived, showArchivedFamilies]);

  // Reload students when showArchived changes
  useEffect(() => {
    if (teacherShellActive) {
      loadStudents(showArchived);
    }
  }, [teacherShellActive, showArchived]);

  // Reload families when showArchivedFamilies changes
  useEffect(() => {
    if (teacherShellActive) {
      loadFamilies(showArchivedFamilies);
    }
  }, [teacherShellActive, showArchivedFamilies]);

  const loadConfig = async () => {
    try {
      const data = await api.getConfig();
      setConfig(data);
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const loadMessages = async () => {
    try {
      const data = await api.getMessages();
      setMessages(data);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const loadClassLevels = async () => {
    try {
      const data = await api.getClassLevels();
      setClassLevels(data);
    } catch (error) {
      console.error('Error loading class levels:', error);
    }
  };

  const loadStudents = async (includeArchived = false) => {
    try {
      const data = await api.getStudents(includeArchived);
      setStudents(data);
    } catch (error) {
      console.error('Error loading students:', error);
    }
  };

  const loadFamilies = async (includeArchived = false) => {
    try {
      const data = await api.getFamilies(includeArchived);
      setFamilies(data);
      return data;
    } catch (error) {
      console.error('Error loading families:', error);
      return [];
    }
  };

  const loadStatistics = useCallback(async () => {
    try {
      const data = await fetchDashboardStats(teacherInfo?.entitlement?.plan);
      setStats(prev => ({ ...prev, ...data }));
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setStatisticsLoaded(true);
    }
  }, [teacherInfo?.entitlement?.plan]);

  const loadPendingPayments = useCallback(async () => {
    try {
      const data = await api.getPendingPayments();
      setPendingPayments(data);
    } catch (error) {
      console.error('Error loading pending payments:', error);
    } finally {
      setPendingPaymentsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const refreshPayments = () => {
      void Promise.all([loadPendingPayments(), loadStatistics()]);
    };
    window.addEventListener(PAYMENT_DATA_CHANGED_EVENT, refreshPayments);
    return () => window.removeEventListener(PAYMENT_DATA_CHANGED_EVENT, refreshPayments);
  }, [isAuthenticated, loadPendingPayments, loadStatistics]);

  // Helper to get current view from URL path
  const getCurrentView = () => {
    const path = location.pathname;
    if (path === APP_HOME_PATH) return 'home';
    if (path === '/students') return 'students';
    if (path === '/students/add' || path === '/students/edit') return 'addStudent';
    if (path === '/families') return 'families';
    if (path === '/families/add' || path === '/families/edit') return 'addFamily';
    if (path === '/payments') return 'payments';
    if (path === '/invoices') return 'generateInvoice';
    if (path === '/course') return 'startCourse';
    if (path === '/statistics') return 'statistics';
    if (path.startsWith('/portfolio')) return 'portfolio';
    if (path.startsWith('/abonnement')) return 'billing';
    if (path.startsWith(SETTINGS_PATH)) return 'settings';
    if (path === NOTIFICATIONS_PATH) return 'notifications';
    if (path === '/models') return 'modelEditor';
    if (path === '/schedules') return 'schedules';
    if (path.startsWith('/examens/nouveau')) return 'tools';
    if (path === '/tools') return 'tools';
    return 'home';
  };

  const currentView = getCurrentView();

  // Navigation helper that mimics setCurrentView behavior
  const setCurrentView = (view, navState) => {
    const viewToPath = {
      'home': APP_HOME_PATH,
      'students': '/students',
      'addStudent': '/students/add',
      'families': '/families',
      'addFamily': '/families/add',
      'createFamily': '/families/add',
      'payments': '/payments',
      'generateInvoice': '/invoices',
      'startCourse': '/course',
      'statistics': '/statistics',
      'portfolio': '/portfolio',
      'billing': '/abonnement',
      'modelEditor': '/models',
      'tools': '/tools',
      'schedules': '/schedules',
      'settings': SETTINGS_PATH,
      'notifications': NOTIFICATIONS_PATH,
      'login': '/login'
    };
    // A path (« /calcul ») navigates as is: tools are addressed by href, not by view id.
    const path = view?.startsWith('/') ? view : viewToPath[view] || APP_HOME_PATH;
    navigate(path, navState ? { state: navState } : undefined);
  };

  useEffect(() => {
    const onProRequired = (event) => {
      const feature = event.detail?.feature;
      navigate(`/abonnement${feature ? `?raison=${encodeURIComponent(feature)}` : ''}`);
    };
    window.addEventListener('billing-pro-required', onProRequired);
    return () => window.removeEventListener('billing-pro-required', onProRequired);
  }, [navigate]);

  const handleLogin = (teacher, { landingPath } = {}) => {
    setAuthAccessError(null);
    setShowTokenWarning(false); // Reset warning on new login
    setShowSessionExpired(false); // Reset session expired overlay
    setIsAuthenticated(true);
    setTeacherInfo(teacher);
    let selectedPlan = null;
    try {
      selectedPlan = localStorage.getItem('np_selected_plan');
      localStorage.removeItem('np_selected_plan');
    } catch { /* Private storage. */ }
    navigate(signupDestination(landingPath) || (selectedPlan === 'accompagnement' ? '/abonnement?offre=expert' : APP_HOME_PATH));
    // Fire-and-forget: sync URSSAF payment statuses in background after fresh login.
    // Errors are intentionally ignored — sync failure must never block login.
    api.syncUrssafPaymentStatuses().catch(() => {});
  };

  // Handle re-authentication from session expired overlay
  const handleReauthenticated = (teacher) => {
    setShowSessionExpired(false);

    // Check if it's a different user - if so, reload everything
    const previousTeacherId = teacherInfo?.teacher_id;
    if (previousTeacherId && teacher.teacher_id !== previousTeacherId) {
      // Different user - full reload to clear previous user's data
      clearSessionState();
      setTeacherInfo(teacher);
      window.location.reload();
      return;
    }

    setTeacherInfo(teacher);
    // Same user - stays on current page with their data
  };

  // Handle full logout from session expired overlay
  const handleSessionLogout = () => {
    setShowSessionExpired(false);
    api.clearToken();
    localStorage.removeItem('teacher_info');
    clearSessionState();
    setIsAuthenticated(false);
    setTeacherInfo(null);
    navigate('/login');
  };

  const handleLogout = async () => {
    await api.logout();
    localStorage.removeItem('teacher_info');
    clearSessionState();
    setIsAuthenticated(false);
    setTeacherInfo(null);
    navigate('/login');
  };

  const handleAddStudent = () => {
    setEditingStudent(null);
    navigate('/students/add');
  };

  const handleEditStudent = (student) => {
    setEditingStudent(student);
    navigate('/students/edit');
  };

  /** REC1: record fiche → form opened on one step (`?step=`). */
  const editPath = (path, stepId) => (stepId ? `${path}?${new URLSearchParams({ [FORM_STEP_PARAM]: stepId })}` : path);
  const editStudentAt = (student, stepId) => {
    setEditingStudent(student);
    navigate(editPath('/students/edit', stepId));
  };
  const editFamilyAt = (family, stepId) => {
    setEditingFamily(family);
    navigate(editPath('/families/edit', stepId));
  };
  const formStepId = new URLSearchParams(location.search).get(FORM_STEP_PARAM);

  const handleSaveStudent = async (studentData, academicHistoryData = null, overrideStudentId = null) => {
    try {
      let studentId;
      const effectiveEditId = editingStudent?.student_id || overrideStudentId;

      if (effectiveEditId) {
        await api.updateStudent(effectiveEditId, studentData);
        studentId = effectiveEditId;
      } else {
        const result = await api.createStudent(studentData);
        studentId = result.student_id;
      }

      // Save academic history if provided (for new students)
      if (academicHistoryData && studentId && !effectiveEditId) {
        try {
          // Convert the simplified form data to API format
          const grades = Object.entries(academicHistoryData.subject_grades || {})
            .filter(([_, value]) => value !== '' && value !== null)
            .map(([subject, grade]) => ({
              subject,
              grade_type: 'annual',
              grade: parseFloat(grade),
              out_of: 20
            }));

          const historyPayload = {
            student_id: studentId,
            entry_type: academicHistoryData.entry_type || 'school_year',
            school_year: academicHistoryData.school_year,
            class_level: academicHistoryData.class_level,
            grade_mode: academicHistoryData.entry_type === 'school_year' ? 'annual' : null,
            diploma_type: academicHistoryData.diploma_type || null,
            diploma_global_grade: academicHistoryData.diploma_global_grade
              ? parseFloat(academicHistoryData.diploma_global_grade) : null,
            moyenne_generale: academicHistoryData.moyenne_generale
              ? parseFloat(academicHistoryData.moyenne_generale) : null,
            notes: academicHistoryData.notes || null,
            grades
          };

          await api.createAcademicHistory(historyPayload);
        } catch (historyError) {
          console.error('Error saving academic history:', historyError);
          // Don't throw - student was created successfully
        }
      }

      const wasEditing = !!editingStudent;
      await loadStudents(showArchived);
      await loadStatistics();
      setEditingStudent(null);
      if (wasEditing) navigate('/students');
      return studentId;
    } catch (error) {
      console.error('Error saving student:', error);
      throw error;
    }
  };

  const handleArchiveStudent = async (studentId) => {
    try {
      await api.archiveStudent(studentId);
      await loadStudents(showArchived);
      await loadStatistics();
    } catch (error) {
      console.error('Error archiving student:', error);
      throw error;
    }
  };

  const handleUnarchiveStudent = async (studentId) => {
    try {
      await api.unarchiveStudent(studentId);
      await loadStudents(showArchived);
      await loadStatistics();
    } catch (error) {
      console.error('Error unarchiving student:', error);
      throw error;
    }
  };

  const handleAddFamilyForStudent = () => {
    setEditingFamily(null);
    navigate('/families/add');
  };

  // Clear the pending family id when leaving the student add route
  useEffect(() => {
    if (location.pathname !== '/students/add') {
      setPendingStudentFamilyId(null);
    }
  }, [location.pathname]);

  const handleEditFamily = (family) => {
    setEditingFamily(family);
    navigate('/families/edit');
  };

  const handleSaveFamily = async (familyData, { urssaf: urssafIntent = null } = {}) => {
    try {
      let newFamilyId = null;
      const wasEditing = editingFamily;

      if (wasEditing) {
        await api.updateFamily(wasEditing.family_id, familyData);
      } else {
        const result = await api.createFamily(familyData);
        newFamilyId = result?.family_id;
      }

      await loadFamilies();
      setEditingFamily(null);

      if (wasEditing && urssafIntent) {
        // Banner « Inscrire / Renouveler »: edits saved, register at once
        setUrssafPostModalPath('/families');
        setUrssafEditMode(false);
        setUrssafModalMode({ autoStart: true, isRenew: urssafIntent === URSSAF_INTENT.RENEW });
        setUrssafModalFamilyId(wasEditing.family_id);
      } else if (!wasEditing && newFamilyId) {
        // New family: always offer URSSAF registration, then proceed to student registration
        setPendingStudentFamilyId(newFamilyId);
        setUrssafPostModalPath('/students/add');
        setUrssafModalFamilyId(newFamilyId);
      } else if (wasEditing && !wasEditing.urssaf_family_id_enc) {
        // Edited family without URSSAF registration: offer inscription
        // If coming from "Revenir", keep the existing post-modal path; otherwise go to /families
        if (!urssafEditMode) {
          setUrssafPostModalPath('/families');
        }
        setUrssafEditMode(false);
        setUrssafModalFamilyId(wasEditing.family_id);
      } else {
        setUrssafEditMode(false);
        navigate('/families');
      }
    } catch (error) {
      console.error('Error saving family:', error);
      throw error;
    }
  };

  // URS2 inbox « Réessayer » (registration): /families?urssaf_register=<id> reopens the modal once.
  useEffect(() => {
    const familyId = parseRegisterLink(new URLSearchParams(location.search));
    const family = familyId && families.find(f => f.family_id === familyId);
    if (!family) return;
    setUrssafPostModalPath('/families');
    setUrssafModalMode({ autoStart: true, isRenew: family.urssaf_state !== URSSAF_STATE.NOT_REGISTERED });
    setUrssafModalFamilyId(familyId);
    navigate(location.pathname, { replace: true });
  }, [location.search, families]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUrssafModalDone = (action, link) => {
    const familyId = urssafModalFamilyId;
    setUrssafModalFamilyId(null);
    setUrssafModalMode(null);

    if (action === 'space') {
      // « Créer l'espace famille »: the family fiche, invitation tab
      setUrssafEditMode(false);
      navigate(familySpacePath(familyId));
      return;
    }
    if (action === 'pay') {
      // URS2 unpaid balance: payment form, family + unpaid lessons selected
      setUrssafEditMode(false);
      navigate(link);
      return;
    }

    if (action === 'edit') {
      // User wants to go back and fix the family data
      const family = families.find(f => f.family_id === familyId);
      setUrssafEditMode(true);
      setEditingFamily(family || null);
      navigate('/families/edit');
    } else {
      // Continue (success, skip, or "continuer quand même")
      const path = urssafPostModalPath;
      setUrssafEditMode(false);
      setUrssafPostModalPath('/families');
      if (path === '/students/add') {
        setPendingStudentFamilyId(familyId);
      }
      navigate(path);
    }
  };

  const handleDeleteFamily = async (familyId) => {
    try {
      await api.deleteFamily(familyId);
      await loadFamilies();
    } catch (error) {
      throw error;
    }
  };

  const handleArchiveFamily = async (familyId) => {
    try {
      await api.archiveFamily(familyId);
      await loadFamilies(showArchivedFamilies);
    } catch (error) {
      throw error;
    }
  };

  const handleUnarchiveFamily = async (familyId) => {
    try {
      await api.unarchiveFamily(familyId);
      await loadFamilies(showArchivedFamilies);
    } catch (error) {
      throw error;
    }
  };

  const handleCourseComplete = async () => {
    await loadStudents();
    await loadStatistics();
    setSelectedStudentForCourse(null);
    navigate(APP_HOME_PATH);
  };

  const handleSelectStudentForCourse = (student) => {
    setSelectedStudentForCourse(student);
  };

  const [studentDetailTab, setStudentDetailTab] = useSessionState(SESSION_STATE_KEY.STUDENT_DETAIL_TAB);

  const handleSelectStudentForDetail = (student, tab) => {
    setSelectedStudentForDetail(student);
    setStudentDetailTab(tab || null);
    navigate(studentPath(student.student_id));
  };

  const handleSelectFamilyForDetail = (family) => {
    setSelectedFamilyForDetail(family);
    navigate(familyPath(family.family_id));
  };

  const handleNavigateToCourseWithSession = (session) => {
    setSessionToRestore(session);
    setSelectedStudentForCourse(session.student);
    navigate('/course');
  };

  const handleSelectFamilyForInvoice = (family, preset) => {
    setSelectedFamilyForInvoice(family);
    navigate('/invoices', { state: { familyId: family?.family_id, preset } });
  };

  const handleOpenPaymentForFamily = (family) => {
    setSelectedFamilyForPayment(family);
    navigate('/payments');
  };

  /** NEW1 « + » menu: `payload` = student / family to preselect, `null` = the teacher picks on the page. */
  const handleNewAction = (action, payload) => {
    switch (action) {
      case NEW_ACTION.LESSON:
        setSelectedStudentForCourse(payload);
        navigate('/course');
        break;
      case NEW_ACTION.FAMILY:
        handleAddFamilyForStudent();
        break;
      case NEW_ACTION.STUDENT:
        handleAddStudent();
        setPendingStudentFamilyId(payload?.family_id ?? null);
        break;
      case NEW_ACTION.INVOICE:
        handleSelectFamilyForInvoice(payload);
        break;
      case NEW_ACTION.PAYMENT:
        handleOpenPaymentForFamily(payload);
        break;
      case NEW_ACTION.EXAM:
        navigate(toolById('exam-builder').href);
        break;
      default:
        break;
    }
  };

  // Clear payment pre-selection when leaving /payments
  useEffect(() => {
    if (location.pathname !== '/payments') {
      setSelectedFamilyForPayment(null);
    }
  }, [location.pathname]);

  // Clear schedule pre-selection when leaving /schedules
  useEffect(() => {
    if (location.pathname !== '/schedules') {
      setSchedulePrefill(null);
    }
  }, [location.pathname]);

  // NeoProf Formations / Courses: its own hosts (or /formations, /courses in dev), first in line
  // so a formations host can never resolve as a teacher portfolio.
  const formationsRoute = resolveFormationsRoute({
    hostname: window.location.hostname, pathname: location.pathname,
  });
  if (formationsRoute) {
    return (
      <StandalonePage footerContent={null}>
        <Route path={`${formationsRoute.basePath}/*`} element={<FormationsApp route={formationsRoute} />} />
      </StandalonePage>
    );
  }

  // Public teacher portfolio (accessible without login).
  // A teacher owns one subdomain and one portfolio per subject:
  // <teacher>.neoprof.fr/ is the default one, <teacher>.neoprof.fr/<subject> the others.
  // Localhost and Vercel previews have no subdomain and fall back to
  // /p/:teacher/:subject? — resolvePortfolioRoute covers both, and returns null
  // for every route that is not a portfolio.
  // Host-based, so it runs before every path-based branch: "/" on a teacher host is the portfolio, not the landing.
  if (resolvePortfolioRoute({ hostname: window.location.hostname, pathname: location.pathname })) {
    return (
      <StandalonePage footerContent={null}>
        <Route path="/p/:teacher/:subject" element={<PortfolioPage />} />
        <Route path="/p/:teacher" element={<PortfolioPage />} />
        <Route path="*" element={<PortfolioPage />} />
      </StandalonePage>
    );
  }

  // Public story pages are independent of the teacher session.
  if (STANDALONE_PATH.COLD_HANDS.test(location.pathname)) {
    return (
      <StandalonePage>
        <Route path="/story/cold-hands" element={<ColdHandsPage />} />
        <Route path="/story/cold-hands/teachers" element={<ColdHandsPage view="teachers" />} />
        <Route path="/story/cold-hands/teachers/:lang" element={<ColdHandsPage view="teachers" />} />
        <Route path="/story/cold-hands/:edition/:season" element={<ColdHandsPage />} />
      </StandalonePage>
    );
  }

  // Family space PWA (PWA1): its own device cookie, independent of the teacher session.
  if (STANDALONE_PATH.FAMILY.test(location.pathname)) {
    return (
      <StandalonePage footerContent={null}>
        <Route path="/family/*" element={<FamilySpaceApp />} />
      </StandalonePage>
    );
  }

  // Public route for shared missions (accessible without login)
  if (STANDALONE_PATH.SHARED_MISSIONS.test(location.pathname)) {
    return (
      <StandalonePage>
        <Route path="/missions/:shareId" element={<SharedMissions />} />
      </StandalonePage>
    );
  }

  // Public route for the practice game link (accessible without login) — T-G2.19.
  // The link id is an opaque UUID; the same shape guard as /missions keeps a
  // malformed path from ever reaching the page.
  if (STANDALONE_PATH.PUBLIC_GAME.test(location.pathname)) {
    return (
      <StandalonePage>
        <Route path="/jouer/:linkId" element={<PublicGame />} />
      </StandalonePage>
    );
  }

  // Public calcul drill + browser-local activity playlists (T-CALC1 / PLAY1).
  // Nothing private: content is public, playlists live in this browser only.
  // `examens` is an allow-list (isPublicExamPath), not a prefix: the teacher
  // routes /examens/nouveau and /examens/attributions/:id live in the
  // authenticated shell below and must fall through this block.
  if (STANDALONE_PATH.CALCUL.test(location.pathname) || isPublicExamPath(location.pathname)) {
    const publicPage = (
      <StandalonePage footerContent={<>
        {showsCalculTutorBand(location.pathname) ? <TutorBand landing={landingById('neocalcul')} /> : null}
        <Footer content={isPublicExamPath(location.pathname) ? EXAM_FOOTER : CALCUL_FOOTER} appearance="station" />
      </>}>
        <Route path="/calcul" element={<CalculHome />} />
        <Route path="/calcul/tables-de-multiplication" element={<CalculGuidePage />} />
        <Route path="/calcul/priorites-operatoires-5e" element={<CalculGuidePage />} />
        <Route path="/calcul/conversion-unites" element={<CalculGuidePage />} />
        <Route path="/calcul/calcul-mental-6e" element={<CalculGuidePage />} />
        <Route path="/calcul/calcul-mental-3e" element={<CalculGuidePage />} />
        <Route path="/calcul/nombres-en-lettres" element={<NombresEnLettres />} />
        <Route path="/calcul/rapport" element={<CalculReport />} />
        <Route path="/calcul/jouer" element={<CalculHome />} />
        <Route path="/calcul/scratch" element={<ScratchAtelier />} />
        <Route path="/calcul/:operation" element={<CalculHome />} />
        <Route path="/calcul/:operation/jouer" element={<CalculPlay />} />
        <Route path="/calcul/:operation/imprimer" element={<CalculPrint />} />
        <Route path="/activites" element={<ActivityPlaylistsPage />} />
        <Route path="/activites/playlists/:playlistId/jouer" element={<ActivityPlaylistPlayPage />} />
        <Route path="/examens" element={<ExamensHome />} />
        <Route path={EXAM_BAC_PATH} element={<ExamensHome initialExam="bac" />} />
        <Route path={EXAM_CATALOG_PATH} element={<ExamResourceCatalog />} />
        <Route path="/examens/epreuve/:sessionId" element={<ExamPlay />} />
        <Route path="/examens/resultat/:resultId" element={<ExamResult />} />
        <Route path="/examens/:diploma/:year/:paperCode" element={<ExamPaperPage />} />
        <Route path="/examens/:diploma" element={<ExamSubjects />} />
      </StandalonePage>
    );
    return isPublicExamPath(location.pathname)
      ? <Suspense fallback={<PublicPageLoader />}><PublicExamExperience>{publicPage}</PublicExamExperience></Suspense>
      : publicPage;
  }

  // Public route for the per-class placement test (accessible without login) —
  // T-G2.27. Unlike /jouer there is no opaque id to shape-check: both segments
  // are words, so the guard here only keeps obviously-hostile paths (slashes,
  // dots, traversal) from ever reaching the page. The ALLOW-LIST that actually
  // decides is server-side, in `placement_service.build_public_test`:
  // /test/maths/M2 and /test/maths/../../etc both come back 404 and render as
  // "test introuvable".
  if (STANDALONE_PATH.PLACEMENT_TEST.test(location.pathname)) {
    return (
      <StandalonePage>
        <Route path="/test/:subject/:classLevel" element={<PublicTest />} />
      </StandalonePage>
    );
  }

  // Public route for invoice verification (accessible without login)
  // Use case-insensitive match and handle with/without trailing content
  const isVerifyInvoiceRoute = STANDALONE_PATH.VERIFY_INVOICE.test(location.pathname);
  if (isVerifyInvoiceRoute) {
    return (
      <StandalonePage>
        <Route path="/verify/invoice/:invoiceUuid" element={<InvoiceVerification />} />
        {/* Fallback for /verify/invoice without UUID */}
        <Route path="/verify/invoice" element={<InvoiceVerification />} />
      </StandalonePage>
    );
  }

  // Exam hosts (annales., bac., brevet.): their entry pages; deeper /examens/… paths use the chamber block.
  const examHostRoute = resolveExamHostRoute({ hostname: window.location.hostname, pathname: location.pathname });
  if (examHostRoute) {
    return (
      <StandalonePage footerContent={<Footer content={EXAM_FOOTER} appearance="station" />}>
        <Route path="*" element={<ExamHostPage route={examHostRoute} />} />
      </StandalonePage>
    );
  }

  // Prerendered marketing pages (LAND3 D-1): the tree the prerender wrote, so the HTML hydrates.
  // "/" is always the landing; the signed-in Accueil lives at APP_HOME_PATH.
  if (isMarketingPath(location.pathname, window.location.hostname)) {
    return <MarketingShell path={location.pathname} pages={MARKETING_PAGES} fallback={<PublicPageLoader />} />;
  }

  const landingRoute = resolveLandingRoute({ hostname: window.location.hostname, pathname: location.pathname });
  if (landingRoute?.preview || landingRoute?.landing.host) {
    return <MarketingShell path={landingPageKey(landingRoute.landing)} pages={MARKETING_PAGES} fallback={<PublicPageLoader />} />;
  }

  // LAND5 funnel landings (paid/social traffic): client-rendered, outside the prerender.
  if (STANDALONE_PATH.GROWTH_LANDING.test(location.pathname)) {
    return (
      <StandalonePage>
        <Route path="*" element={<Navigate to="/" replace />} />
      </StandalonePage>
    );
  }

  // Public routes for legal pages (accessible without login)
  const isLegalRoute = STANDALONE_PATH.LEGAL.test(location.pathname);
  if (isLegalRoute) {
    return (
      <StandalonePage>
        <Route path="/legal-mentions" element={<LegalMentions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/terms-of-sale" element={<TermsOfSale />} />
        <Route path="/avance-immediate" element={<AvanceImmediate />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/payment" element={<Navigate to="/abonnement" replace />} />
        <Route path="/tools" element={<ToolsPage isAuthenticated={false} />} />
      </StandalonePage>
    );
  }

  // Public: lesson pages are indexable explanations, open to anyone.
  if (STANDALONE_PATH.LESSONS.test(location.pathname)) {
    return (
      <StandalonePage>
        <Route path="/lessons/:slug" element={<LessonPage />} />
        <Route path="/lessons" element={<ToolsPage />} />
      </StandalonePage>
    );
  }

  // Public: a pupil sits an assigned exam from a link, with no account. The
  // link identifies a recipient, never a device.
  if (STANDALONE_PATH.EXAM_SITTING.test(location.pathname)) {
    return (
      <StandalonePage>
        <Route path="/examens/passer/:token" element={<ExamSittingPage />} />
      </StandalonePage>
    );
  }

  // Public route for theme demo (accessible without login)
  if (STANDALONE_PATH.THEME_DEMO.test(location.pathname)) {
    return (
      <StandalonePage>
        <Route path="/theme-demo" element={<ThemeDemo />} />
      </StandalonePage>
    );
  }

  // Public editorial pages stay available even when a teacher has a session.
  if (STANDALONE_PATH.EDITORIAL.test(location.pathname)) {
    return (
      <StandalonePage>
        <Route path="/blog" element={<EditorialPage />} />
        <Route path="/blog/:slug" element={<EditorialPage />} />
        <Route path="/questions-frequentes" element={<EditorialPage />} />
        <Route path="/nos-valeurs" element={<EditorialPage />} />
        <Route path="/comparatif/logiciel-avance-immediate-cours-particuliers" element={<EditorialPage />} />
      </StandalonePage>
    );
  }

  // Show loading while checking if refresh token is still valid
  if (authChecking) {
    return (
      <div className="App App--standalone">
        <PublicPageLoader />
      </div>
    );
  }

  // Show public landing page if not authenticated
  if (!isAuthenticated) {
    return (
      <StandalonePage>
        <Route path={APP_HOME_PATH} element={<Navigate to={PUBLIC_PATH.LOGIN} replace />} />
        <Route path="/login" element={<Login onLoginSuccess={handleLogin} />} />
        <Route path="/login/verify" element={<Login onLoginSuccess={handleLogin} />} />
        <Route path="/register" element={<Register onRegisterSuccess={handleLogin} />} />
        <Route path="/register/verify" element={<Register onRegisterSuccess={handleLogin} />} />
        <Route path="/legal-mentions" element={<LegalMentions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/terms-of-sale" element={<TermsOfSale />} />
        <Route path="/avance-immediate" element={<AvanceImmediate />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/payment" element={<Navigate to="/login" replace />} />
        <Route path="/verify-invoice/:uuid" element={<InvoiceVerification />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </StandalonePage>
    );
  }

  function navigateSchedule(prefill) {
    setSchedulePrefill(prefill && typeof prefill === 'object' && !prefill.student_id
      ? prefill
      : { student: prefill || null });
    navigate('/schedules');
  }

  /** Ctrl/⌘K palette handlers (runProposal). Relance runs on Accueil, where the email composer lives. */
  const paletteHandlers = {
    setCurrentView,
    onNavigateTool: navigate,
    onOpenPayment: handleOpenPaymentForFamily,
    onSelectFamily: handleSelectFamilyForInvoice,
    onSelectStudent: handleSelectStudentForCourse,
    onViewStudentDetail: handleSelectStudentForDetail,
    onViewFamilyDetail: handleSelectFamilyForDetail,
    onAddFamilyForStudent: handleAddFamilyForStudent,
    onAddStudent: handleAddStudent,
    onNavigateSchedule: navigateSchedule,
    onSetStatsTab: setStatsActiveTab,
    onSetStatsScrollTo: setStatsScrollTo,
    onRelance: (family) => navigate(APP_HOME_PATH, { state: { relance: family?.family_id ?? RELANCE_ALL } }),
    openSettings: (section) => navigate(settingsPath(section)),
  };

  const renderRoutes = () => (
    <Routes>
      <Route path={APP_HOME_PATH} element={
        <Home
          stats={stats}
          statisticsLoading={!statisticsLoaded}
          students={students}
          families={families}
          teacherInfo={teacherInfo}
          setCurrentView={setCurrentView}
          onLogout={handleLogout}
          messages={messages}
          pendingPayments={pendingPayments}
          pendingPaymentsLoading={!pendingPaymentsLoaded}
          onSelectStudent={handleSelectStudentForCourse}
          onViewStudentDetail={handleSelectStudentForDetail}
          onViewFamilyDetail={handleSelectFamilyForDetail}
          onNavigateSchedule={navigateSchedule}
          onNavigateToCourse={handleNavigateToCourseWithSession}
        />
      } />

      <Route path="/students" element={
        <StudentList
          students={students}
          families={families}
          classLevels={classLevels}
          onArchive={handleArchiveStudent}
          onUnarchive={handleUnarchiveStudent}
          onEdit={handleEditStudent}
          onStudentSelect={handleSelectStudentForDetail}
          setCurrentView={setCurrentView}
          onStartCourseWithStudent={handleSelectStudentForCourse}
          messages={messages}
          showArchived={showArchived}
          setShowArchived={setShowArchived}
          onStudentUpdated={() => loadStudents(showArchived)}
          onRosterImported={() => Promise.all([loadFamilies(showArchivedFamilies), loadStudents(showArchived)])}
        />
      } />

      <Route path="/students/add" element={
        formPage(PEOPLE_COPY.studentsTitle,
        <StudentForm
          student={editingStudent}
          families={families}
          students={students}
          classLevels={classLevels}
          onSubmit={handleSaveStudent}
          onCreateFamily={handleAddFamilyForStudent}
          onOpenFamilySpace={(familyId) => navigate(familySpacePath(familyId))}
          onEditFamily={handleEditFamily}
          onCancel={() => {
            setEditingStudent(null);
            navigate('/students');
          }}
          isEditing={!!editingStudent}
          teacherId={teacherInfo?.teacher_id}
          preSelectedFamilyId={pendingStudentFamilyId}
        />)
      } />

      <Route path="/students/edit" element={
        formPage(PEOPLE_COPY.studentsTitle,
        <StudentForm
          student={editingStudent}
          families={families}
          students={students}
          classLevels={classLevels}
          onSubmit={handleSaveStudent}
          onCreateFamily={handleAddFamilyForStudent}
          onOpenFamilySpace={(familyId) => navigate(familySpacePath(familyId))}
          onEditFamily={handleEditFamily}
          onCancel={() => {
            setEditingStudent(null);
            navigate('/students');
          }}
          isEditing={!!editingStudent}
          initialStepId={formStepId}
          teacherId={teacherInfo?.teacher_id}
        />)
      } />

      <Route path={RECORD_PATH.LEGACY_STUDENT_DETAIL} element={
        <Navigate to={selectedStudentForDetail ? studentPath(selectedStudentForDetail.student_id) : RECORD_PATH.STUDENTS} replace />
      } />

      <Route path={RECORD_PATH.STUDENT_DETAIL} element={
        <RecordDetailRoute
          param="studentId"
          idKey="student_id"
          records={students}
          sessionRecord={selectedStudentForDetail}
          fallbackPath={RECORD_PATH.STUDENTS}
          render={(student) => (
        <StudentRecord
          onPlanLesson={navigateSchedule}
          onOpenPayment={handleOpenPaymentForFamily}
          onSelectForInvoice={handleSelectFamilyForInvoice}
          key={student.student_id}
          student={student}
          families={families}
          initialTab={studentDetailTab}
          onBack={() => {
            setSelectedStudentForDetail(null);
            setStudentDetailTab(null);
            navigate('/students');
          }}
          onEdit={editStudentAt}
          onEditFamily={editFamilyAt}
          onStartCourse={(student) => {
            setSelectedStudentForCourse(student);
            navigate('/course');
          }}
        />
          )}
        />
      } />

      <Route path="/families" element={
        <FamilyList
          families={families}
          students={students}
          onEdit={handleEditFamily}
          onArchive={handleArchiveFamily}
          onUnarchive={handleUnarchiveFamily}
          onSelectFamily={handleSelectFamilyForDetail}
          setCurrentView={setCurrentView}
          onSelectFamilyForInvoice={handleSelectFamilyForInvoice}
          onOpenPayment={handleOpenPaymentForFamily}
          messages={messages}
          showArchived={showArchivedFamilies}
          setShowArchived={setShowArchivedFamilies}
          onRosterImported={() => Promise.all([loadFamilies(showArchivedFamilies), loadStudents(showArchived)])}
        />
      } />

      <Route path={RECORD_PATH.LEGACY_FAMILY_DETAIL} element={
        <Navigate to={selectedFamilyForDetail ? familyPath(selectedFamilyForDetail.family_id) : RECORD_PATH.FAMILIES} replace />
      } />

      <Route path={RECORD_PATH.FAMILY_DETAIL} element={
        <RecordDetailRoute
          param="familyId"
          idKey="family_id"
          records={families}
          sessionRecord={selectedFamilyForDetail}
          fallbackPath={RECORD_PATH.FAMILIES}
          render={(family) => (
        <FamilyRecord
          onPlanLesson={navigateSchedule}
          onOpenPayment={handleOpenPaymentForFamily}
          onStartCourse={(student) => { setSelectedStudentForCourse(student); navigate('/course'); }}
          key={family.family_id}
          family={family}
          students={students}
          initialTab={new URLSearchParams(location.search).get(FAMILY_TAB_PARAM)}
          onBack={() => {
            setSelectedFamilyForDetail(null);
            navigate('/families');
          }}
          onEdit={editFamilyAt}
          onEditStudent={editStudentAt}
          onSelectForInvoice={handleSelectFamilyForInvoice}
          onSelectStudent={handleSelectStudentForDetail}
          onArchive={handleArchiveFamily}
        />
          )}
        />
      } />

      <Route path="/families/add" element={
        formPage(PEOPLE_COPY.familiesTitle,
        <FamilyForm
          family={editingFamily}
          onSubmit={handleSaveFamily}
          onCancel={() => {
            setEditingFamily(null);
            navigate('/families');
          }}
          messages={messages}
          isEditing={!!editingFamily}
          teacherId={teacherInfo?.teacher_id}
        />)
      } />

      <Route path="/families/edit" element={
        formPage(PEOPLE_COPY.familiesTitle,
        <FamilyForm
          family={editingFamily}
          onSubmit={handleSaveFamily}
          onCancel={() => {
            setEditingFamily(null);
            navigate('/families');
          }}
          messages={messages}
          isEditing={!!editingFamily}
          initialStepId={formStepId}
          teacherId={teacherInfo?.teacher_id}
        />)
      } />
<Route path={TEACHER_FAMILY_REQUESTS_PATH} element={
  <LegacyRequestsRedirect />
} />
      <Route path="/payments" element={
        <PaymentsPage families={families} students={students}>
          <PaymentForm
            families={families}
            students={students}
            teacherInfo={teacherInfo}
            preSelectedFamily={selectedFamilyForPayment}
            preSelectedAmount={selectedFamilyForPayment?.preAmount}
            paymentPrefill={location.state?.paymentPrefill}
            urssafRetry={parseRetryLink(new URLSearchParams(location.search))}
            onUrssafRetryOpened={() => navigate(location.pathname, { replace: true })}
            onCancel={() => navigate(APP_HOME_PATH)}
            onViewLogs={() => { setStatsActiveTab('logs'); navigate('/statistics'); }}
          />
        </PaymentsPage>
      } />

      <Route path="/invoices" element={
        <PageFrame width={PAGE_WIDTH.NARROW}>
        <PageFrame.Header title={INVOICE_COPY.title} />
        <InvoiceGenerator
          families={families}
          students={students}
          preSelectedFamily={selectedFamilyForInvoice}
          teacherInfo={teacherInfo}
          onCancel={() => {
            setSelectedFamilyForInvoice(null);
            navigate('/families');
          }}
          onViewLogs={() => { setStatsActiveTab('logs'); navigate('/statistics'); }}
        />
        </PageFrame>
      } />

      <Route path="/course" element={
        <CourseSession
          // New key per history entry: « + » while already on /course starts a fresh séance.
          key={location.key}
          resumeOnMount={navigationType === 'POP'}
          onNavigate={navigate}
          students={students}
          families={families}
          config={config}
          teacherInfo={teacherInfo}
          messages={messages}
          preSelectedStudent={selectedStudentForCourse}
          sessionToRestore={sessionToRestore}
          onComplete={() => {
            setSessionToRestore(null);
            handleCourseComplete();
          }}
          onCancel={() => {
            setSelectedStudentForCourse(null);
            setSessionToRestore(null);
            navigate(APP_HOME_PATH);
          }}
          onAddStudent={handleAddStudent}
          onAddFamilyForStudent={handleAddFamilyForStudent}
        />
      } />

      <Route path="/missions" element={<MissionLibrary students={students} />} />

      <Route path="/schedules" element={
        <ScheduleManager
          students={students}
          families={families}
          prefill={location.state?.schedulePrefill || schedulePrefill}
          editId={location.state?.scheduleEditId}
          readOnly={teacherInfo?.entitlement?.plan === 'free'}
        />
      } />

      <Route path="/statistics" element={
        teacherInfo?.entitlement?.plan === 'free' ? <StatisticsLock /> : <Statistics
          stats={stats}
          students={students}
          families={families}
          pendingPayments={pendingPayments}
          teacherInfo={teacherInfo}
          onArchiveFamily={handleArchiveFamily}
          activeTab={statsActiveTab}
          onTabChange={setStatsActiveTab}
          scrollToId={statsScrollTo}
          onScrollToDone={() => setStatsScrollTo(null)}
        />
      } />

      <Route path="/models" element={<ModelEditorPage />} />
      <Route path="/portfolio" element={<PortfolioDashboard />} />
      {/* One route for new and existing pages: creating a page must not remount the wizard. */}
      <Route path="/portfolio/:id" element={<PortfolioEditor teacher={teacherInfo} />} />


      <Route path="/examens/nouveau" element={<ExamWizardPage />} />
      <Route path="/examens/nouveau/:configurationId" element={<ExamWizardPage />} />
      <Route path="/examens/suivi" element={<ExamMonitorPage />} />
      <Route path="/examens/attributions/:assignmentId" element={<ExamMonitorPage />} />
      <Route path="/tools" element={<ToolsPage isAuthenticated />} />

      <Route path="/legal-mentions" element={<LegalMentions />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/terms-of-sale" element={<TermsOfSale />} />
      <Route path="/avance-immediate" element={<AvanceImmediate />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/payment" element={<Navigate to="/abonnement" replace />} />
      <Route path="/abonnement" element={<BillingPage />} />
      <Route path="/abonnement/retour" element={<BillingPage />} />
      <Route path={NOTIFICATIONS_PATH} element={<NotificationsPage />} />
      <Route path={SETTINGS_PATH} element={<SettingsPage families={families} />} />
      <Route path={`${SETTINGS_PATH}/:section`} element={<SettingsPage families={families} />} />

      <Route path="/theme-demo" element={<ThemeDemo />} />

      {/* Catch-all route */}
      <Route path="*" element={<Navigate to={APP_HOME_PATH} replace />} />
    </Routes>
  );

  return (
    <InboxProvider key={teacherInfo?.teacher_id} enabled={teacherShellActive} teacherId={teacherInfo?.teacher_id}>
    <CommandPaletteProvider enabled={Boolean(teacherInfo)} students={students} families={families} stats={stats}
      handlers={paletteHandlers}>
    <GuideDemoProvider>
    <div className="App">
      <NetworkBanner />

      {/*
        * The shell chrome gets its own boundary, separate from the one around
        * the routes below. Both halves are lazy, but they suspend on different
        * schedules: the chrome resolves once and then never again, while a
        * route suspends the first time a teacher opens each page. Sharing one
        * boundary would tear the navigation off the screen on every one of
        * those first visits.
        */}
      <Suspense fallback={null}>
        {showSessionExpired && (
          <SessionExpiredOverlay
            onReauthenticated={handleReauthenticated}
            onLogout={handleSessionLogout}
          />
        )}
        {urssafModalFamilyId && (
          <UrssafRegistrationModal
            familyId={urssafModalFamilyId}
            onDone={handleUrssafModalDone}
            isNewFamily={urssafPostModalPath === '/students/add'}
            {...urssafModalMode}
          />
        )}
        {showTokenWarning && (
          <TokenExpirationWarning
            remainingTime={tokenRemainingTime}
            onDismiss={() => setShowTokenWarning(false)}
            onLogout={handleLogout}
          />
        )}
        <Navigation
          currentView={currentView}
          setCurrentView={setCurrentView}
          teacherInfo={teacherInfo}
          onLogout={handleLogout}
          newMenu={{ families, students, onAction: handleNewAction }}
        />
        <FirstUseGuide key={teacherInfo?.teacher_id} path={guidePathFor(location.pathname)} teacherId={teacherInfo?.teacher_id} onNavigate={navigate} />
      </Suspense>

      <div className="app-main-column">
        <ShellMain>
          <div className="main-content__routes">
            <RecordNavigationProvider
              students={students}
              families={families}
              onOpenStudent={handleSelectStudentForDetail}
              onOpenFamily={handleSelectFamilyForDetail}
            >
              <Suspense fallback={<PublicPageLoader />}>
                {renderRoutes()}
              </Suspense>
            </RecordNavigationProvider>
          </div>
        </ShellMain>
      </div>

      <Suspense fallback={null}>
        {teacherInfo?.entitlement?.trial_end_notice_due ? <TrialEndNotice entitlement={teacherInfo.entitlement} /> : null}
        <GlobalCourseNotification
          students={students}
          currentView={currentView}
          onNavigateToCourse={handleNavigateToCourseWithSession}
        />
      </Suspense>
    </div>
    </GuideDemoProvider>
    </CommandPaletteProvider>
    </InboxProvider>
  );
}

export default App;
